import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import listen from "@insight/toolkit-utils/lib/events/listen";
import FadeIn from '../IAHeaderMegaMenu/FadeIn'
import { NIA_ENV } from '../../api/common/constants'
import { useHeaderInfoContext } from '../../context/HeaderInfoContext'

export default function NiaContainer({
                                       isOpen,
                                       setIsOpen,
                                       headerInfo,
                                       customCatalogData,
                                       featureFlags,
                                     }) {
  const containerRef = useRef(null)
  const sdkRef = useRef(null) // Ref to store the SDK instance
  const sdkLoadedRef = useRef(false) // Ref to track if SDK is already loaded
  const previousSessionIdRef = useRef(null) // Ref to track previous sessionId
  const [isLoaded, setIsLoaded] = useState(false)
  const { refreshHeaderInfo } = useHeaderInfoContext()

  const getNiaUrl = () => {
    // Get the NIA environment from feature flag, default to QA
    const niaEnvironment = (featureFlags && featureFlags[NIA_ENV]) || 'QA'

    switch (niaEnvironment.toUpperCase()) {
      case 'DEV':
        return 'https://nia-dev.insight.com'
      case 'PROD':
        return 'https://nia.insight.com'
      case 'QA':
      default:
        return 'https://nia-qa.insight.com'
    }
  }

  // Token refresh callback for SDK mode
  const handleTokenRefresh = async () => {

    try {
      // Use the existing refreshHeaderInfo from context
      const refreshedHeaderInfo = await refreshHeaderInfo()

      // Return the new JWT token from refreshed header info
      return refreshedHeaderInfo?.jwtToken

    } catch (error) {
      console.error('Token refresh failed:', error)

      // Let NIA handle gracefully - it will continue without auth header
      throw error
    }
  }

  const handleClick = () => {
    setIsOpen(false)
  }

  useEffect(() => {
    const handleTokenExpiring = async (event) => {

      if (event.data.type === 'Nia:tokenExpiring') {
        try {
          // Refresh header information using context
          const refreshedHeaderInfo = await refreshHeaderInfo()

          // Post message to NIA SDK iframe using window.postMessage
          const message = {
            type: 'Nia:data',
            payload: {
              userContext: {
                headerInfo: refreshedHeaderInfo,
                customCatalogData,
              },
              pageContext: {
                pageUrl: window.location.href
              },
              sessionContext: {
                session_id: refreshedHeaderInfo?.sessionId
              }
            }
          };

          // Find the NIA iframe and post message to it
          const niaIframe = containerRef.current?.querySelector('iframe');
          if (niaIframe && niaIframe.contentWindow) {
            niaIframe.contentWindow.postMessage(message, '*');
          } else {
            // Fallback: post to all frames
            window.postMessage(message, '*');
          }
        } catch (error) {
          console.error('Token refresh failed:', error)
        }
      }
    }

    window.addEventListener('message', handleTokenExpiring)
    return () => window.removeEventListener('message', handleTokenExpiring)
  }, [customCatalogData, refreshHeaderInfo])

  useEffect(() => {
    const loadSDK = async ({ restoreSession }) => {
      if (!containerRef.current || sdkLoadedRef.current) return

      console.log("loading NIA SDK")
      containerRef.current.id = `nia-sdk-${crypto.randomUUID()}`

      try {
        if (!window.NiaLoader) {
          const niaUrl = getNiaUrl()
          const script = document.createElement('script')
          script.src = `${niaUrl}/sdk/nia-loader.js`
          await new Promise((resolve, reject) => {
            script.onload = resolve
            script.onerror = reject
            document.head.appendChild(script)
          })
        }

        const sdkInstance = await window.NiaLoader.load({
          apiEndpoint: getNiaUrl(),
          parentOrigin: window.location.origin,
          containerId: containerRef.current.id,
          initialRoute: '/',
          theme: 'light',
          restoreSession: restoreSession || false,
          userData: {
            userContext: {
              headerInfo,
              customCatalogData,
            },
            pageContext: {
              "pageUrl": window.location.href
            },
            sessionContext: {
              "session_id": headerInfo?.sessionId
            }
          },
          // Add token refresh callback for SDK mode (complements iframe mode)
          onTokenRefresh: handleTokenRefresh,
          onReady: () => {
            console.log('SDK Ready!')
            setIsLoaded(true)
          },
          onError: (error) => {
            console.error('SDK Error:', error)
          }
        })

        sdkRef.current = sdkInstance
        sdkLoadedRef.current = true // Mark SDK as loaded
      } catch (error) {
        console.error('Failed to load SDK:', error)
      }
    }

    if (isOpen) {
      const currentSessionId = headerInfo?.sessionId
      const storedSessionId = window.localStorage.getItem('NiaSessionId')
      let restoreSession

      if (!storedSessionId) {
        // First time login - no previous session to restore
        restoreSession = false
      } else if (storedSessionId !== currentSessionId) {
        // Session changed - user logged out/in or switched accounts
        restoreSession = false
        window.localStorage.removeItem('NiaConversation')
      } else {
        // Same session - restore conversation if it exists
        restoreSession = true
      }

      // Store current sessionId for future comparisons
      currentSessionId && window.localStorage.setItem('NiaSessionId', currentSessionId)

      loadSDK({ restoreSession })
      // Update the previous sessionId reference
      previousSessionIdRef.current = currentSessionId
    }

    // Cleanup
    return () => {
      if (sdkRef.current) {
        sdkRef.current.destroy()
        sdkRef.current = null
        sdkLoadedRef.current = false // Reset SDK loaded state on cleanup
      }
    }
  }, [isOpen, headerInfo?.sessionId])

  useEffect(() => {
    return listen('Nia:close', () => {
      handleClick()
    })
  }, [])

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      // Add delay to prevent immediate closing when opening
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, 100)

      return () => {
        clearTimeout(timeoutId)
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [isOpen, setIsOpen])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }

    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="c-nia-container">
      <FadeIn delay={5}>
        <div className={cn('c-modal', { 'is-open': isOpen })}>
          <div className="c-modal-container">
            <div
              ref={containerRef}
              style={{
                height: '100%',
                border: '1px solid #ccc',
                borderRadius: '8px'
              }}
            />
          </div>
        </div>
      </FadeIn>
    </div>
  )
}
